import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useEmployees } from '@/hooks/use-employees';
import { useUpdateProject } from '@/hooks/use-projects';
import { cn } from '@/lib/utils';
import type { ProjectDetail } from '@hygge/shared';
import { PROJECT_COLORS } from '@hygge/shared';
import { useEffect, useState } from 'react';
import { ManagerPicker } from './create-project-dialog';
import { useAuthStore } from '@/stores/auth';
import { useBoards } from '@/hooks/use-boards';
import { httpStatus } from '@/lib/http-status';
import type { ArchiveBoardChoice } from '@/lib/projects';
import { ArchiveBoardsChoice } from './archive-boards-choice';
import { boardsReaderMaySet } from '../lib/project-boards';
import { useFreshOnOpen } from '../hooks/use-fresh-on-open';

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

interface EditProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDetail;
}

export function EditProjectDialog({ open, onOpenChange, project }: EditProjectDialogProps) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [status, setStatus] = useState<string>(project.status);
  // Archiving from here asks the same question about the boards as the Archive button.
  const [archiveBoards, setArchiveBoards] = useState<ArchiveBoardChoice | null>(null);
  const archiving = status === 'archived' && project.status !== 'archived';
  const {
    data: visibleBoards = [],
    isError: isBoardsError,
    error: boardsError,
    refetch: refetchBoards,
  } = useBoards({ projectId: project.id }, { enabled: open && archiving });
  // The archive question waits for a list fetched afresh when it is asked.
  const boardsFresh = useFreshOnOpen(open && archiving ? project.id : null, refetchBoards);
  const isLoadingBoards = open && archiving && !boardsFresh;
  // Asked only about the boards the archive would actually change.
  const projectBoards = boardsReaderMaySet(visibleBoards);
  const needsBoardsChoice = archiving && projectBoards.length > 0;
  // Refused outright (no Open Boards) is an answer — there is nothing to ask about.
  const boardsLoadFailed = archiving && isBoardsError && httpStatus(boardsError) !== 403;
  // The project's managers are its members marked `isManager`. This was one
  // picker prefilled from a pointer that went stale, so saving the dialog for
  // any reason re-appointed whoever the pointer named (HW-969).
  const initialManagerIds = project.managers.map((m) => m.id);
  const [managerIds, setManagerIds] = useState<string[]>(initialManagerIds);
  // Appointing yourself is refused by the server, and the whole save with it —
  // so you are not offered. A super_admin may; a manager already may stay one.
  const me = useAuthStore((s) => s.user);
  const notOffered = me?.employee?.id && me.role !== 'super_admin' ? [me.employee.id] : [];
  // As on New Project, a project keeps at least one manager. One that has none
  // already (an archived one, say) may still be edited without adding any.
  const leavesNoManager = initialManagerIds.length > 0 && managerIds.length === 0;
  const [color, setColor] = useState(project.color);
  const [isBillable, setIsBillable] = useState(project.isBillable);
  const [engagementType, setEngagementType] = useState<string>(project.engagementType ?? 'unset');
  const updateMutation = useUpdateProject();

  const { data: employeesData } = useEmployees({ limit: 500, statuses: ['active', 'probation'] });
  const employees = employeesData?.data ?? [];

  useEffect(() => {
    if (open) {
      setName(project.name);
      setDescription(project.description || '');
      setStatus(project.status);
      setArchiveBoards(null);
      setManagerIds(project.managers.map((m) => m.id));
      setColor(project.color);
      setIsBillable(project.isBillable);
      setEngagementType(project.engagementType ?? 'unset');
    }
  }, [open, project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    await updateMutation.mutateAsync({
      id: project.id,
      name: name.trim(),
      description: description.trim() || null,
      status,
      ...(needsBoardsChoice && archiveBoards ? { archiveBoards } : {}),
      // Sent only when changed: the list replaces the managers exactly, and an
      // untouched dialog must not rewrite them.
      ...(sameSet(managerIds, initialManagerIds) ? {} : { managerIds }),
      color,
      isBillable,
      engagementType: engagementType === 'unset' ? undefined : engagementType,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Project</DialogTitle>
          <DialogDescription>Update project details</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-desc">Description</Label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            {(needsBoardsChoice || boardsLoadFailed) && (
              <ArchiveBoardsChoice
                boards={projectBoards}
                value={archiveBoards}
                onChange={setArchiveBoards}
                loadFailed={boardsLoadFailed}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Project Managers</Label>
            <ManagerPicker
              employees={employees}
              known={project.managers}
              notOffered={notOffered}
              value={managerIds}
              onChange={setManagerIds}
            />
            {leavesNoManager && (
              <p className="text-xs text-destructive">A project needs at least one manager.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              {PROJECT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={cn(
                    'h-7 w-7 rounded-full cursor-pointer transition-all',
                    color === c
                      ? 'ring-2 ring-offset-2 ring-offset-background ring-foreground scale-110'
                      : 'hover:scale-105',
                  )}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {/* Outstaff vs outsource decides how the Time Tracker asks for time on
              this project: outstaff people log against the project alone, an
              outsource project expects a board and a task. Null stays legal —
              an unclassified project claims neither. */}
          <div className="space-y-2">
            <Label htmlFor="edit-engagement">Engagement</Label>
            <Select value={engagementType} onValueChange={setEngagementType}>
              <SelectTrigger id="edit-engagement">
                <SelectValue placeholder="Not classified" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">Not classified</SelectItem>
                <SelectItem value="outstaff">Outstaff — hours go to the project alone</SelectItem>
                <SelectItem value="outsource">Outsource — boards and tasks</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* The project's billable default. Time entries inherit it, so this
              one switch is what decides whether an hour logged here counts as
              billable on a time report — there is no per-entry exception to set
              anywhere yet, and every entry reads this. */}
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="edit-billable">Billable work</Label>
              <p className="text-xs text-muted-foreground">
                Off for internal projects — tooling, R&amp;D, our own product. Hours logged here
                are then excluded from billable time reports.
              </p>
            </div>
            <Switch
              id="edit-billable"
              checked={isBillable}
              onCheckedChange={setIsBillable}
              className="mt-0.5"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !name.trim() ||
                leavesNoManager ||
                // Not until the boards are in: saving early would archive without the question.
                (archiving && (isLoadingBoards || boardsLoadFailed)) ||
                (needsBoardsChoice && !archiveBoards) ||
                updateMutation.isPending
              }
              className="cursor-pointer"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
